"use client";

import { useEffect, useId, useRef, useState, type MouseEvent } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { usePortalTranslations } from "../i18n/client";
import { portalSectionAtScroll } from "../lib/portalScrollSpy";
import styles from "./PortalNavigation.module.css";

export type PortalNavigationLink = {
  section: string;
  href: string;
  label: string;
  homeSectionId?: string;
};

export function PortalNavigation({ links, home, section, onNavigate, tabs = false, contentTabs = false }: { links: PortalNavigationLink[]; home: boolean; section: string; onNavigate?: () => void; tabs?: boolean; contentTabs?: boolean }) {
  const t = usePortalTranslations();
  const router = useRouter();
  const navigationRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const moreTrigger = useRef<HTMLButtonElement>(null);
  const morePanel = useRef<HTMLDivElement>(null);
  const moreId = useId();
  const [moreOpen, setMoreOpen] = useState(false);
  const pendingAnchor = useRef<string | null>(null);
  const settledAnchor = useRef<{ id: string; scrollY: number } | null>(null);
  const initialHashHandled = useRef(false);
  const [activeHomeSection, setActiveHomeSection] = useState<string | null>(null);
  const [clickedPageSection, setClickedPageSection] = useState<string | null>(null);
  const overflowLinks = contentTabs ? links.filter((link) => !["overview", "employees", "reviews"].includes(link.section)) : [];
  const selectedOverflow = overflowLinks.find((link) => link.section === section);

  useEffect(() => {
    if (!moreOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !moreTrigger.current?.contains(event.target) && !morePanel.current?.contains(event.target)) setMoreOpen(false);
    };
    const closeEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMoreOpen(false);
      moreTrigger.current?.focus({ preventScroll: true });
    };
    const close = () => setMoreOpen(false);
    const closeDesktop = () => { if (window.matchMedia("(min-width:1101px)").matches) close(); };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeEscape);
    window.addEventListener("popstate", close);
    window.addEventListener("resize", closeDesktop);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeEscape);
      window.removeEventListener("popstate", close);
      window.removeEventListener("resize", closeDesktop);
    };
  }, [moreOpen]);

  useEffect(() => {
    const navigation = navigationRef.current;
    const page = navigation?.closest<HTMLElement>("[data-portal-page]");
    if (!tabs || !navigation || !page) return;

    const previousHeight = page.style.getPropertyValue("--portal-tabs-height");
    const previousNavigationHeight = page.style.getPropertyValue("--portal-navigation-height");
    const previousAccountWidth = page.style.getPropertyValue("--portal-account-reserved-width");
    const previousStuck = page.dataset.portalNavigationStuck;
    const account = page.querySelector<HTMLElement>("[data-portal-account]");
    const publishHeight = () => {
      const height = `${navigation.getBoundingClientRect().height}px`;
      page.style.setProperty("--portal-tabs-height", height);
      page.style.setProperty("--portal-navigation-height", height);
      const accountWidth = account?.getBoundingClientRect().width ?? 0;
      page.style.setProperty("--portal-account-reserved-width", `${accountWidth > 0 ? accountWidth + 20 : 0}px`);
    };
    const updateSticky = () => {
      const stuck = String(navigation.getBoundingClientRect().top <= 0);
      navigation.dataset.stuck = stuck;
      page.dataset.portalNavigationStuck = stuck;
    };
    const observer = new ResizeObserver(publishHeight);
    publishHeight();
    updateSticky();
    observer.observe(navigation);
    if (account) observer.observe(account);
    window.addEventListener("scroll", updateSticky, { passive: true });
    window.addEventListener("resize", updateSticky);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", updateSticky);
      window.removeEventListener("resize", updateSticky);
      if (previousHeight) page.style.setProperty("--portal-tabs-height", previousHeight);
      else page.style.removeProperty("--portal-tabs-height");
      if (previousNavigationHeight) page.style.setProperty("--portal-navigation-height", previousNavigationHeight);
      else page.style.removeProperty("--portal-navigation-height");
      if (previousAccountWidth) page.style.setProperty("--portal-account-reserved-width", previousAccountWidth);
      else page.style.removeProperty("--portal-account-reserved-width");
      if (previousStuck !== undefined) page.dataset.portalNavigationStuck = previousStuck;
      else delete page.dataset.portalNavigationStuck;
    };
  }, [tabs, links.length]);

  useEffect(() => {
    const navigation = navigationRef.current;
    const page = navigation?.closest("[data-portal-page]");
    const homepage = page?.querySelector("[data-portal-home]");
    const header = page?.querySelector<HTMLElement>("[data-portal-header]");
    if (!home || !navigation || !homepage) {
      initialHashHandled.current = false;
      return;
    }

    const sections = [...homepage.querySelectorAll<HTMLElement>("[data-portal-home-section]")];
    const sectionLinks = new Map(links.filter((link) => link.homeSectionId).map((link) => [link.homeSectionId, link.section]));
    let frame: number | null = null;

    function updateActiveSection() {
      frame = null;
      if (!navigation) return;
      const navigationHeight = window.getComputedStyle(navigation).position === "sticky"
        ? navigation.getBoundingClientRect().height
        : header && window.getComputedStyle(header).position === "fixed" ? header.getBoundingClientRect().height : 0;
      const positions = sections.filter((element) => sectionLinks.has(element.id)).map((element) => ({ element, bounds: element.getBoundingClientRect() }));
      const documentHeight = document.documentElement.scrollHeight;
      const atBottom = documentHeight > window.innerHeight + 1 && window.scrollY + window.innerHeight >= documentHeight - 2;

      // Preserve a clicked, visible destination until the page actually moves again.
      // Several anchors can share the same maximum scroll position on short pages.
      if (settledAnchor.current) {
        const destination = positions.find(({ element }) => element.id === settledAnchor.current?.id);
        if (Math.abs(window.scrollY - settledAnchor.current.scrollY) <= 2 && destination && destination.bounds.bottom > navigationHeight && destination.bounds.top < window.innerHeight) return;
        settledAnchor.current = null;
      }

      // Keep the clicked destination selected while smooth scrolling passes other sections.
      if (pendingAnchor.current) {
        const destination = positions.find(({ element }) => element.id === pendingAnchor.current);
        const destinationTop = destination ? Number.parseFloat(window.getComputedStyle(destination.element).scrollMarginTop) || navigationHeight : 0;
        const arrived = destination && (
          Math.abs(destination.bounds.top - destinationTop) <= 2
          || (atBottom && destination.bounds.top < window.innerHeight && destination.bounds.bottom > navigationHeight)
        );
        if (destination && !arrived) return;
        if (destination) {
          setActiveHomeSection(sectionLinks.get(destination.element.id) ?? null);
          settledAnchor.current = { id: destination.element.id, scrollY: atBottom ? documentHeight - window.innerHeight : window.scrollY };
          pendingAnchor.current = null;
          return;
        }
        pendingAnchor.current = null;
      }

      const current = portalSectionAtScroll(positions.map(({ element, bounds }) => ({ id: element.id, top: bounds.top, bottom: bounds.bottom })), {
        scrollY: window.scrollY,
        viewportHeight: window.innerHeight,
        documentHeight,
        navigationHeight,
      });
      setActiveHomeSection(current ? sectionLinks.get(current) ?? null : null);
    }

    function scheduleUpdate() {
      if (frame === null) frame = window.requestAnimationFrame(updateActiveSection);
    }

    function resumeScrollTracking() {
      pendingAnchor.current = null;
      scheduleUpdate();
    }

    function trackHash() {
      const id = window.location.hash.slice(1);
      pendingAnchor.current = sectionLinks.has(id) ? id : null;
      settledAnchor.current = null;
      scheduleUpdate();
    }

    function handleScrollKey(event: KeyboardEvent) {
      if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(event.key)) resumeScrollTracking();
    }

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(navigation);
    if (header) resizeObserver.observe(header);
    resizeObserver.observe(homepage);
    sections.forEach((element) => resizeObserver.observe(element));
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("hashchange", trackHash);
    window.addEventListener("wheel", resumeScrollTracking, { passive: true });
    window.addEventListener("touchstart", resumeScrollTracking, { passive: true });
    window.addEventListener("keydown", handleScrollKey);
    if (!initialHashHandled.current) {
      initialHashHandled.current = true;
      trackHash();
    } else scheduleUpdate();

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("hashchange", trackHash);
      window.removeEventListener("wheel", resumeScrollTracking);
      window.removeEventListener("touchstart", resumeScrollTracking);
      window.removeEventListener("keydown", handleScrollKey);
    };
  }, [home, links]);

  const activeSection = contentTabs ? section : clickedPageSection ?? (home ? activeHomeSection ?? (tabs ? links.find((link) => link.homeSectionId)?.section : undefined) : section);

  useEffect(() => {
    const navigation = tabs ? scrollRef.current : navigationRef.current;
    if (!navigation) return;
    let frame = 0;
    const keepActiveLinkVisible = () => {
      frame = 0;
      if (!tabs && !window.matchMedia("(min-width: 1101px)").matches) return;
      const selectedLink = navigation.querySelector<HTMLElement>("[aria-current]");
      const activeLink = selectedLink?.getBoundingClientRect().width ? selectedLink : navigation.querySelector<HTMLElement>('[data-active="true"]');
      if (!activeLink) return;
      const bounds = navigation.getBoundingClientRect();
      const activeBounds = activeLink.getBoundingClientRect();
      const offset = activeBounds.left < bounds.left
        ? activeBounds.left - bounds.left - 12
        : activeBounds.right > bounds.right
          ? activeBounds.right - bounds.right + 12
          : 0;

      // Scroll overflowing header links without moving the page or keyboard focus.
      if (offset) navigation.scrollBy({
        left: offset,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth",
      });
    };
    const scheduleUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(keepActiveLinkVisible);
    };
    const observer = new ResizeObserver(scheduleUpdate);
    observer.observe(navigation);
    scheduleUpdate();
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, [activeSection, tabs]);

  useEffect(() => {
    const resetPageSelection = () => setClickedPageSection(null);
    window.addEventListener("pageshow", resetPageSelection);
    return () => window.removeEventListener("pageshow", resetPageSelection);
  }, []);

  function navigate(event: MouseEvent<HTMLAnchorElement>, link: PortalNavigationLink) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (contentTabs) {
      setMoreOpen(false);
      const navigation = navigationRef.current;
      if (navigation?.dataset.stuck === "true") {
        event.preventDefault();
        // Let the destination route position its own panel after rendering, including
        // transitions between the compact booking header and the expanded portal hero.
        router.push(`${link.href.split("#")[0]}#portal-content`);
      }
      onNavigate?.();
      return;
    }
    settledAnchor.current = null;
    if (home && link.href.startsWith("#") && link.homeSectionId) {
      pendingAnchor.current = link.homeSectionId;
      setClickedPageSection(null);
      setActiveHomeSection(link.section);
    } else {
      pendingAnchor.current = null;
      setClickedPageSection(link.section);
    }
    onNavigate?.();
  }

  if (!links.length) return null;
  const NavigationLink = contentTabs ? Link : "a";

  return <nav ref={navigationRef} className={`${styles.navigation} ${tabs ? styles.tabs : ""}`} aria-label={t("portal.navigation.label")} data-portal-navigation={tabs ? "tabs" : undefined}>
    <div className={styles.inner}><div ref={scrollRef} className={styles.links}>
      {links.map((link) => <NavigationLink
        key={link.section}
        href={link.href}
        {...(contentTabs ? { scroll: false, prefetch: false } : {})}
        className={`${styles.link} ${overflowLinks.includes(link) ? styles.overflowLink : ""}`}
        onClick={(event) => navigate(event, link)}
        aria-current={activeSection === link.section ? home ? "location" : "page" : undefined}
      ><span>{link.label}</span><svg className={styles.arrow} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14m-6-6 6 6-6 6" /></svg></NavigationLink>)}
      {overflowLinks.length ? <button ref={moreTrigger} className={`${styles.link} ${styles.moreTrigger}`} type="button" aria-expanded={moreOpen} aria-controls={moreId} aria-label={selectedOverflow ? t("portal.navigation.moreCurrent", { section: selectedOverflow.label }) : undefined} data-active={Boolean(selectedOverflow)} onClick={() => setMoreOpen((open) => !open)}>
        <span>{t("portal.navigation.more")}</span><svg className={styles.moreChevron} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </button> : null}
    </div>
      {moreOpen ? <div ref={morePanel} id={moreId} className={styles.morePanel} aria-label={t("portal.navigation.more")}>
        {overflowLinks.map((link) => <Link key={link.section} href={link.href} scroll={false} prefetch={false} className={styles.moreLink} onClick={(event) => navigate(event, link)} aria-current={section === link.section ? "page" : undefined}>
          <span>{link.label}</span>
        </Link>)}
      </div> : null}
    </div>
  </nav>;
}
